import { Capacitor } from '@capacitor/core';

/**
 * 扩展的平台信息接口
 */
export interface PlatformInfo {
  isNative: boolean;
  isWeb: boolean;
  isElectron: boolean;
  os: string;
  userAgent: string;
  isIOS: boolean;
  isAndroid: boolean;
  isMacOS: boolean;
  isWindows: boolean;
  isLinux: boolean;
  isPWA: boolean;
  isStandalone: boolean;
  displayMode: string;
  isSafari: boolean;
  processPlatform?: string; // 仅在 Electron 环境中可用
}

/**
 * 获取完整的平台信息
 * 统一所有平台检测逻辑的核心函数
 */
export function getPlatformInfo(): PlatformInfo {
  const userAgent = navigator.userAgent;

  // 检测Electron环境
  const isElectron = window && userAgent.toLowerCase().indexOf('electron') > -1;

  let isNative = false;
  let isWeb = false;

  // 只有在非 Electron 环境下才检测 Capacitor 平台
  if (!isElectron) {
    isNative = Capacitor.isNativePlatform();
    isWeb = !isNative;
  } else {
    // 在 Electron 环境中，isNative 和 isWeb 都应为 false
    isNative = false;
    isWeb = false; // Electron 不是典型的 Web 浏览器环境
  }

  // 获取 Capacitor 平台信息
  const capacitorPlatform = Capacitor.getPlatform();

  // 详细平台检测
  const isIOS = capacitorPlatform === 'ios' || /iPad|iPhone|iPod/.test(userAgent);
  const isAndroid = capacitorPlatform === 'android';
  const isMacOS = capacitorPlatform === 'web' && /Mac|Macintosh/.test(userAgent);
  const isWindows = capacitorPlatform === 'web' && /Win/.test(userAgent);
  const isLinux = capacitorPlatform === 'web' && /Linux/.test(userAgent);

  // PWA 和 Standalone 检测
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                      (window.navigator as any).standalone === true ||
                      document.referrer.includes('android-app://');

  const isPWA = isStandalone || window.location.href.includes('?mode=pwa');

  // 显示模式检测
  const displayMode = window.matchMedia('(display-mode: standalone)').matches
    ? 'standalone'
    : 'browser';

  // Electron 环境下的 process.platform（如果可用）
  let processPlatform: string | undefined;
  if (isElectron && typeof process !== 'undefined' && process.platform) {
    processPlatform = process.platform;
  }

  return {
    isNative,
    isWeb,
    isElectron,
    os: capacitorPlatform,
    userAgent,
    isIOS,
    isAndroid,
    isMacOS,
    isWindows,
    isLinux,
    isPWA,
    isStandalone,
    displayMode,
    isSafari: isSafari(),
    processPlatform
  };
}

/**
 * 获取当前 rem 配置（像素值）
 * 根据 v3.css 和 V3RouteHandler 的逻辑
 */
export function getCurrentRemConfig(): number {
  const htmlElement = document.documentElement;
  const isV3Enabled = htmlElement.classList.contains('v3-enabled');
  const isIOSPlatform = htmlElement.classList.contains('ios-platform');

  if (!isV3Enabled) {
    // 非 v3 页面，使用浏览器默认值（通常是 16px）
    return 16;
  }

  if (isIOSPlatform) {
    // iOS 平台：1rem = 16px
    return 16;
  } else {
    // 其他平台：1rem = 14px
    return 14;
  }
}

/**
 * 获取计算后的根字体大小
 * 直接从 DOM 中读取当前的根字体大小
 */
export function getComputedRootFontSize(): number {
  const rootFontSize = window.getComputedStyle(document.documentElement).fontSize;
  return parseFloat(rootFontSize);
}

// ============ 便捷的平台检测函数 ============

let _cachedPlatformInfo: PlatformInfo | null = null;

/**
 * 获取缓存的平台信息（避免重复计算）
 */
function getCachedPlatformInfo(): PlatformInfo {
  if (!_cachedPlatformInfo) {
    _cachedPlatformInfo = getPlatformInfo();
  }
  return _cachedPlatformInfo;
}

/**
 * 清除平台信息缓存（测试或特殊情况下使用）
 */
export function clearPlatformInfoCache(): void {
  _cachedPlatformInfo = null;
}

/**
 * 检查是否为原生平台
 */
export function isNativePlatform(): boolean {
  return getCachedPlatformInfo().isNative;
}

/**
 * 检查是否为 Web 平台
 */
export function isWebPlatform(): boolean {
  return getCachedPlatformInfo().isWeb;
}

/**
 * 检查是否为 Electron 平台
 */
export function isElectronPlatform(): boolean {
  return getCachedPlatformInfo().isElectron;
}

/**
 * 检查是否为 iOS 平台
 */
export function isIOSPlatform(): boolean {
  return getCachedPlatformInfo().isIOS;
}

/**
 * 检查是否为 Android 平台
 */
export function isAndroidPlatform(): boolean {
  return getCachedPlatformInfo().isAndroid;
}

/**
 * 检查是否为 PWA 模式
 */
export function isPWAMode(): boolean {
  return getCachedPlatformInfo().isPWA;
}

/**
 * 检查是否为独立模式
 */
export function isStandaloneMode(): boolean {
  return getCachedPlatformInfo().isStandalone;
}

/**
 * 获取平台操作系统标识
 */
export function getPlatformOS(): string {
  return getCachedPlatformInfo().os;
}

/**
 * 获取用户代理字符串
 */
export function getUserAgent(): string {
  return getCachedPlatformInfo().userAgent;
}

// ============ PWA 相关函数 ============

/**
 * 检查浏览器是否支持 PWA 安装
 */
export function isPwaInstallSupported(): boolean {
  return (
    'serviceWorker' in navigator &&
    window.matchMedia('(display-mode: browser)').matches
  );
}

/**
 * 检查是否有已注册的 Service Worker
 */
export async function hasRegisteredServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    return registrations.length > 0;
  } catch (error) {
    console.error('Error checking service worker registrations:', error);
    return false;
  }
}

/**
 * 获取完整的 PWA 状态信息
 */
export async function getPwaStatus(): Promise<{
  isPwaMode: boolean;
  isInstallSupported: boolean;
  hasServiceWorker: boolean;
  displayMode: string;
  isStandalone: boolean;
}> {
  const platformInfo = getCachedPlatformInfo();
  const hasServiceWorker = await hasRegisteredServiceWorker();

  return {
    isPwaMode: platformInfo.isPWA,
    isInstallSupported: isPwaInstallSupported(),
    hasServiceWorker,
    displayMode: platformInfo.displayMode,
    isStandalone: platformInfo.isStandalone
  };
}

/**
 * 监听 PWA 安装状态变化
 */
export function watchPwaInstallStatus(
  callback: (event: MediaQueryListEvent) => void
): () => void {
  const mediaQuery = window.matchMedia('(display-mode: standalone)');

  // 添加监听器
  if (mediaQuery.addEventListener) {
    mediaQuery.addEventListener('change', callback);
    return () => mediaQuery.removeEventListener('change', callback);
  } else {
    // 兼容旧版浏览器
    mediaQuery.addListener(callback);
    return () => mediaQuery.removeListener(callback);
  }
}

// ============ 触控设备检测函数 ============

/**
 * 检查是否为触控设备
 * 整合了多种检测方法以提高准确性
 */
export function isTouchDevice(): boolean {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
}

/**
 * 检查是否为移动设备
 * 结合触控检测和平台信息
 */
export function isMobileDevice(): boolean {
  const platformInfo = getCachedPlatformInfo();
  return isTouchDevice() && (platformInfo.isIOS || platformInfo.isAndroid);
}

/**
 * 检查是否为 Safari 浏览器
 * 包括 iOS Safari 和 macOS Safari，但排除基于 WebKit 的其他浏览器如 Chrome
 */
export function isSafari(): boolean {
  const userAgent = navigator.userAgent;
  return /Safari/.test(userAgent) && !/Chrome/.test(userAgent) && !/Chromium/.test(userAgent);
}

/**
 * 检查是否支持触觉反馈
 * 优先检查 Capacitor Haptics API，降级到浏览器 vibrate API
 */
export function supportsTactileFeedback(): boolean {
  return (
    isTouchDevice() &&
    (Capacitor.isNativePlatform() || 'vibrate' in navigator)
  );
}
